import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Mic2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LEVEL = ["Unseen", "Exposed", "Familiar", "Understands", "Can apply", "Can teach"];
let weakCache = null;
const loadWeak = async () => {
  if (weakCache) return weakCache;
  try {
    const { data } = await api.get("/knowledge/concepts");
    weakCache = data.filter(c => c.level <= 2).map(c => ({ concept: c.concept, level: c.level }));
  } catch { weakCache = []; }
  setTimeout(() => { weakCache = null; }, 120000);
  return weakCache;
};

/** When an ARIA reply touches a concept she is still shaky on (level ≤ 2), offer a one-tap drill. */
export function DrillSuggestion({ text, messageIndex }) {
  const navigate = useNavigate();
  const [hits, setHits] = useState([]);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    if (!text) return;
    loadWeak().then(weak => {
      const lower = text.toLowerCase();
      const found = weak.filter(w => lower.includes(w.concept.toLowerCase())).sort((a, b) => a.level - b.level).slice(0, 2);
      setHits(found);
    });
  }, [text]);

  if (hits.length === 0) return null;
  const start = async (concept) => {
    setStarting(concept);
    try {
      const { data } = await api.post("/interviews", { target: `Review: ${concept}`, topic_focus: [concept] });
      navigate(`/app/interviews/${data.interview_id}`);
    } catch { toast.error("Could not start drill"); setStarting(null); }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`drill-suggestion-${messageIndex}`}>
      <span className="text-[11px] text-[var(--aria-text-muted)]">You're still {LEVEL[hits[0].level].toLowerCase()} on this —</span>
      {hits.map(h => (
        <button key={h.concept} onClick={() => start(h.concept)} disabled={!!starting} data-testid={`drill-now-${h.concept}`}
          className="aria-pill text-xs px-3 py-1 inline-flex items-center gap-1.5 border-[var(--aria-primary)]/50 hover:border-[var(--aria-primary)] hover:bg-[var(--aria-primary)]/10 disabled:opacity-50">
          {starting === h.concept ? <Loader2 size={11} className="animate-spin" /> : <Mic2 size={11} className="text-[var(--aria-primary)]" />}
          Drill {h.concept} now
        </button>
      ))}
    </div>
  );
}
